import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import type { Supplier } from '@/services/suppliersService';

function supplierLabel(s: Supplier): string {
  const code = String(s.code || '').trim();
  const name = String(s.name || '').trim();
  if (code && name) return `${code} · ${name}`;
  return name || code || String(s.id);
}

type Props = {
  suppliers: Supplier[];
  value: string;
  onChange: (supplierId: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  className?: string;
};

export function SupplierSearchSelect({
  suppliers,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  className = '',
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: supplierLabel(s),
        searchText: `${s.code || ''} ${s.name || ''} ${s.id}`.toLowerCase(),
      })),
    [suppliers]
  );

  const selected = options.find((o) => o.value === value);
  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter((o) => o.searchText.includes(normalizedQuery));
  }, [options, normalizedQuery]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative min-w-[14rem] flex-1 ${className}`}>
      <div className="flex h-[38px] w-full items-center rounded-md border border-gray-300 bg-white text-sm shadow-sm focus-within:border-stocker-primary focus-within:ring-1 focus-within:ring-stocker-primary">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 text-left"
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
            {selected?.label || placeholder}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        </button>
        {value ? (
          <button
            type="button"
            className="mr-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label={t('common.clear', { defaultValue: 'Limpiar' })}
            onClick={() => onChange('')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="absolute z-50 mt-0.5 w-full rounded-md bg-white py-0.5 text-sm shadow-lg ring-1 ring-black/5">
          <div className="border-b border-gray-100 px-2 py-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className="block w-full rounded border border-gray-200 py-1.5 pl-7 pr-2 text-xs focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filtered.length === 1) {
                    e.preventDefault();
                    choose(filtered[0].value);
                  }
                }}
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-52 overflow-auto py-0.5">
            {filtered.length === 0 ? (
              <li className="px-2.5 py-2 text-center text-xs text-gray-500">
                {t('filters.noResults', { defaultValue: 'Sin resultados' })}
              </li>
            ) : (
              filtered.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className={`flex w-full px-2.5 py-1.5 text-left text-xs hover:bg-stocker-primary/5 ${
                      option.value === value ? 'font-medium text-stocker-primary' : 'text-gray-800'
                    }`}
                    onClick={() => choose(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
